import React from 'react';

import { useMutation } from '@tanstack/react-query';

import { Alert, Box, Button, Stack, Typography } from '@mui/material';

import { useGetReportingLensBlockQuery } from '~/hooks/apis/reporting/useGetReportingLensBlockQuery';
import { estimateLensScan } from '~/hooks/apis/reporting/useLensScanCost';

import { formatScanUsd } from '../cards/scanCost';

import type { LensState } from './types';

/** The scan estimate lives beside the rows it describes, scoped to the current block and filters. */
export default function BlockScanEstimate({
  lensId,
  lensVersion,
  blockId,
  state,
}: {
  lensId: string;
  lensVersion: number;
  blockId: string;
  state: LensState;
}) {
  const lineage = useGetReportingLensBlockQuery(lensId, lensVersion, blockId, state);
  const queries = lineage.data?.queries ?? [];
  const sql = queries.map((query) => query.sql);
  const signature = JSON.stringify({ lensId, lensVersion, blockId, state, sql });
  const check = useMutation({
    mutationFn: async ({ statements }: { statements: Array<string>; signature: string }) => {
      const results = await Promise.all(statements.map((query) => estimateLensScan({ sql: query })));
      return {
        bytes: results.reduce((sum, result) => sum + result.bytesProcessed, 0),
        usd: results.reduce((sum, result) => sum + result.estimatedUsd, 0),
        rate: results[0].usdPerTiB,
      };
    },
    retry: false,
  });
  const current = check.variables?.signature === signature;
  const result = current ? check.data : undefined;
  const available = queries.length > 0 && sql.every((query) => Boolean(query));
  return (
    <Box sx={{ bgcolor: 'action.hover', borderRadius: '8px', p: 1.5 }}>
      <Stack alignItems="center" direction="row" justifyContent="space-between" gap={1}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Estimated data scanned</Typography>
        <Button
          disabled={!available || lineage.isFetching || check.isPending}
          onClick={() => check.mutate({ statements: sql as Array<string>, signature })}
          size="small"
          variant="outlined"
        >
          {check.isPending ? 'Checking…' : result ? 'Run dry run again' : 'Dry run'}
        </Button>
      </Stack>
      <Box aria-live="polite">
        {result ? (
          <>
            <Typography sx={{ fontSize: 22, fontWeight: 600, mt: 0.5 }}>
              {result.bytes > 0 && result.bytes / 1024 ** 3 < 0.001
                ? '< 0.001'
                : (result.bytes / 1024 ** 3).toLocaleString('en-US', { maximumFractionDigits: 3 })}{' '}
              GiB
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12 }}>
              {formatScanUsd(result.usd)} per uncached run · {queries.length}{' '}
              {queries.length === 1 ? 'query' : 'queries'}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 11, mt: 0.5 }}>
              Reference rate US${result.rate}/TiB. Actual billing varies. This is an estimate, not the last run’s usage.
            </Typography>
          </>
        ) : (
          <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.75 }}>
            Check how many GiB the queries behind this chart would scan with its current dates and filters. The check
            does not run the queries.
          </Typography>
        )}
        {lineage.isError && (
          <Alert severity="warning">Could not load this chart’s queries. Reopen Data to try again.</Alert>
        )}
        {!lineage.isLoading && !lineage.isError && !available && (
          <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.5 }}>
            {lineage.data?.sqlWithheld ?? 'A dry run is not available for this data.'}
          </Typography>
        )}
        {current && check.isError && (
          <Alert severity="warning">
            {check.error instanceof Error ? check.error.message : 'Could not estimate this scan.'}
          </Alert>
        )}
      </Box>
    </Box>
  );
}
